"use strict";
/**
 * TRAINING MIX — composizione del lavoro PIANIFICATO di squadra
 * (@leopart/calc), più la stagione sportiva canonica.
 *
 * Modulo Allenamenti (ADR 025, `vault/Decisioni/025-allenamenti-blocco-leggero.md`):
 * i minuti qui dentro sono PIANIFICATI e di SQUADRA, mai per-atleta. Queste
 * funzioni non alimentano sRPE, Recovery Index, ACWR, monotonia o strain — lo
 * vietano il vincolo di dominio e il fatto stesso che l'input (`BloccoMix`)
 * non ha alcun collegamento ad un atleta.
 *
 * Stesso contratto di `AggregatoSquadra` (team-overview.ts): "non pervenuto
 * ≠ zero". `percentuale` e `mediaMinutiPerSeduta` ritornano `null`, mai `0`,
 * quando il denominatore è 0 — un donut a 0% e un donut senza dati sono due
 * fatti diversi.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MESE_INIZIO_STAGIONE = void 0;
exports.stagioneDi = stagioneDi;
exports.finestraDi = finestraDi;
exports.bucketDi = bucketDi;
exports.periodoPrecedente = periodoPrecedente;
exports.chiaveSeduta = chiaveSeduta;
exports.contaSedute = contaSedute;
exports.sommaMinuti = sommaMinuti;
exports.bloccoPiuLungo = bloccoPiuLungo;
exports.percentuale = percentuale;
exports.mediaMinutiPerSeduta = mediaMinutiPerSeduta;
exports.distribuzionePerCategoria = distribuzionePerCategoria;
exports.distribuzionePerSottocategoria = distribuzionePerSottocategoria;
exports.dominante = dominante;
exports.serieTemporale = serieTemporale;
exports.deltaPct = deltaPct;
const calendario_1 = require("./calendario");
const kernel_1 = require("./kernel");
// ─── Stagione sportiva ────────────────────────────────────────────────────
/**
 * Mese di inizio stagione sportiva (1-12). Canonico: luglio.
 *
 * Decisione 2026-08-11 (vedi design M2/M3): è l'UNICA finestra con dati
 * persistiti a terra (`weeklyReports.season`, dentro la chiave unique di
 * upsert e stampata nei PDF già generati). `weekly-reports.deriveSeason`
 * diventa un wrapper su `stagioneDi` — stessi valori, nessun dato toccato.
 */
exports.MESE_INIZIO_STAGIONE = 7;
/**
 * Stagione che contiene `iso`, 12 mesi da `meseInizio` incluso.
 * Generica su `meseInizio` per poter rappresentare — SENZA migrarla — anche
 * la variante "stagione-testing" del registry (1 settembre, oggi però a
 * finestra aperta a destra: quella non è una `Stagione` chiusa come questa).
 */
function stagioneDi(iso, meseInizio = exports.MESE_INIZIO_STAGIONE) {
    const anno = Number(iso.slice(0, 4));
    const mese = Number(iso.slice(5, 7));
    const annoInizio = mese >= meseInizio ? anno : anno - 1;
    const da = `${annoInizio}-${String(meseInizio).padStart(2, '0')}-01`;
    // 12 mesi cumulativi da meseInizio (0-based) → anno/mese di fine, corretti
    // anche quando meseInizio=1 (stagione = anno solare, stesso anno di fine).
    const indiceFine0 = meseInizio - 1 + 11;
    const annoFine = annoInizio + Math.floor(indiceFine0 / 12);
    const meseFine = (indiceFine0 % 12) + 1;
    const a = (0, calendario_1.ultimoDelMese)(`${annoFine}-${String(meseFine).padStart(2, '0')}-01`);
    const etichetta = `${annoInizio}-${String(annoFine % 100).padStart(2, '0')}`;
    return { etichetta, da, a };
}
// ─── Etichette italiane (solo per i periodi di questo file) ───────────────
const MESI_IT = [
    'gen', 'feb', 'mar', 'apr', 'mag', 'giu',
    'lug', 'ago', 'set', 'ott', 'nov', 'dic',
];
const MESI_IT_CAP = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];
function etichettaSettimana(da, a) {
    const giornoA = Number(da.slice(8, 10));
    const giornoB = Number(a.slice(8, 10));
    const meseA = Number(da.slice(5, 7));
    const meseB = Number(a.slice(5, 7));
    const annoA = Number(da.slice(0, 4));
    const annoB = Number(a.slice(0, 4));
    if (annoA === annoB && meseA === meseB) {
        return `${giornoA} – ${giornoB} ${MESI_IT[meseA - 1]} ${annoA}`;
    }
    if (annoA === annoB) {
        return `${giornoA} ${MESI_IT[meseA - 1]} – ${giornoB} ${MESI_IT[meseB - 1]} ${annoA}`;
    }
    return `${giornoA} ${MESI_IT[meseA - 1]} ${annoA} – ${giornoB} ${MESI_IT[meseB - 1]} ${annoB}`;
}
/** Finestra del periodo che contiene `riferimento`. */
function finestraDi(periodo, riferimento) {
    if (periodo === 'week') {
        const da = (0, calendario_1.lunediDi)(riferimento);
        const giorni = (0, calendario_1.giorniDellaSettimana)(da);
        const a = giorni[6];
        return { da, a, etichetta: etichettaSettimana(da, a), granularita: 'day' };
    }
    if (periodo === 'month') {
        const da = (0, calendario_1.primoDelMese)(riferimento);
        const a = (0, calendario_1.ultimoDelMese)(riferimento);
        const mese = Number(da.slice(5, 7));
        const anno = Number(da.slice(0, 4));
        return {
            da,
            a,
            etichetta: `${MESI_IT_CAP[mese - 1]} ${anno}`,
            granularita: 'week',
        };
    }
    const s = stagioneDi(riferimento);
    return { da: s.da, a: s.a, etichetta: `Stagione ${s.etichetta}`, granularita: 'month' };
}
/** Bucket a cui appartiene `iso` per la data granularità (chiave della serie). */
function bucketDi(iso, granularita) {
    if (granularita === 'day')
        return iso;
    if (granularita === 'week')
        return (0, calendario_1.lunediDi)(iso);
    return (0, calendario_1.meseDi)(iso);
}
/**
 * Finestra del periodo IMMEDIATAMENTE PRECEDENTE, stessa "taglia" di `f`.
 * Non serve sapere il `Periodo` originale: la granularità di `f` lo implica
 * uno a uno (`finestraDi` la assegna sempre nello stesso modo), quindi si
 * ricostruisce da lì tornando indietro di un giorno/mese e ricalcolando.
 */
function periodoPrecedente(f) {
    if (f.granularita === 'day') {
        // f era una settimana (7 giorni): la settimana precedente inizia 7 giorni prima.
        return finestraDi('week', (0, calendario_1.sommaGiorni)(f.da, -7));
    }
    if (f.granularita === 'week') {
        // f era un mese: un giorno prima del primo del mese = ultimo del mese precedente.
        return finestraDi('month', (0, calendario_1.sommaGiorni)(f.da, -1));
    }
    // f era una stagione: un giorno prima dell'inizio = ultimo giorno della stagione precedente.
    return finestraDi('season', (0, calendario_1.sommaGiorni)(f.da, -1));
}
/**
 * Chiave di seduta: giorno + fascia. `slot` oggi è sempre `null` (un
 * allenamento al giorno): tutti i blocchi dello stesso giorno condividono la
 * stessa chiave finché la fascia non comparirà nel form (ADR 025).
 */
function chiaveSeduta(localDate, slot) {
    return `${localDate}#${slot !== null && slot !== void 0 ? slot : 'unica'}`;
}
/** Numero di sedute distinte (localDate + slot) fra i blocchi. */
function contaSedute(blocchi) {
    return new Set(blocchi.map((b) => chiaveSeduta(b.localDate, b.slot))).size;
}
/**
 * Somma dei minuti pianificati. `0` su input vuoto è legittimo: è una
 * somma, non una media — non ha il problema "non pervenuto ≠ zero" di
 * `percentuale`/`mediaMinutiPerSeduta`.
 */
function sommaMinuti(blocchi) {
    return blocchi.reduce((s, b) => s + b.durationMinutes, 0);
}
/**
 * Il blocco con più `durationMinutes`. `null` su input vuoto. A parità di
 * minuti vince il primo incontrato (nessun criterio di tie-break dichiarato
 * dal dominio). Generica su `T extends BloccoMix` così il chiamante può
 * passare il proprio tipo esteso (es. con `_id`/`title` di presentazione) e
 * ririceverlo indietro intatto, invece di doverlo solo tradurre a mano.
 */
function bloccoPiuLungo(blocchi) {
    if (blocchi.length === 0)
        return null;
    return blocchi.reduce((max, b) => b.durationMinutes > max.durationMinutes ? b : max);
}
/**
 * parte/totale × 100 a 1 decimale. `null` se il totale è ≤ 0 — MAI `0`: un
 * periodo senza minuti non ha una percentuale pari a zero, non ne ha nessuna.
 */
function percentuale(parte, totale) {
    if (!(totale > 0))
        return null;
    return (0, kernel_1.round)((parte / totale) * 100, 1);
}
/**
 * Media minuti per seduta. `null` se non c'è nessuna seduta — `mean([])` di
 * `kernel.ts` darebbe 0, che qui si leggerebbe come "sedute da zero minuti"
 * invece che "nessun dato": da qui il wrapper.
 */
function mediaMinutiPerSeduta(blocchi) {
    var _a;
    const perSeduta = new Map();
    for (const b of blocchi) {
        const chiave = chiaveSeduta(b.localDate, b.slot);
        perSeduta.set(chiave, ((_a = perSeduta.get(chiave)) !== null && _a !== void 0 ? _a : 0) + b.durationMinutes);
    }
    if (perSeduta.size === 0)
        return { valore: null, base: 0 };
    return {
        valore: (0, kernel_1.round)((0, kernel_1.mean)([...perSeduta.values()]), 1),
        base: perSeduta.size,
    };
}
function distribuzionePer(blocchi, 
/**
 * Le chiavi del blocco. Sono PIÙ d'una: un blocco può stare in più
 * categorie, e i suoi minuti si contano interi su ciascuna.
 */
chiaviDi, 
/** Denominatore della percentuale. Default: somma di `blocchi` stessi. */
totaleOverride) {
    var _a;
    const totale = totaleOverride !== null && totaleOverride !== void 0 ? totaleOverride : sommaMinuti(blocchi);
    const acc = new Map();
    for (const b of blocchi) {
        /*
          `Set` sulle chiavi del singolo blocco: se lo stesso id comparisse due
          volte sullo stesso blocco, i suoi minuti verrebbero contati due volte
          DENTRO la stessa categoria — che è un errore, a differenza del conteggio
          pieno su categorie diverse, che è voluto.
        */
        for (const chiave of new Set(chiaviDi(b))) {
            if (chiave == null || chiave === '')
                continue;
            const corrente = (_a = acc.get(chiave)) !== null && _a !== void 0 ? _a : { minuti: 0, blocchi: 0 };
            corrente.minuti += b.durationMinutes;
            corrente.blocchi += 1;
            acc.set(chiave, corrente);
        }
    }
    return [...acc.entries()]
        .map(([chiave, v]) => ({
        chiave,
        minuti: v.minuti,
        blocchi: v.blocchi,
        percentuale: percentuale(v.minuti, totale),
    }))
        .sort((a, b) => b.minuti - a.minuti);
}
/** Distribuzione minuti/blocchi per categoria (macro), % sul totale del periodo. */
function distribuzionePerCategoria(blocchi) {
    return distribuzionePer(blocchi, (b) => b.categoryIds);
}
/**
 * Distribuzione minuti/blocchi per sottocategoria.
 *
 * Se `categorieAmmesse` è passato (tipicamente le sei macro-categorie
 * `isPhysical`), solo i blocchi con categoria nell'elenco generano una riga
 * (`tecnico`/`tattico`/… restano fuori, come i blocchi senza sottocategoria).
 *
 * La percentuale però è SEMPRE sul totale minuti di **tutti** i blocchi
 * passati, non sul sottoinsieme ammesso: è la stessa base di
 * `distribuzionePerCategoria`. Decisione deliberata (2026-08-12) per
 * coerenza fra i due grafici della stessa schermata — donut per categoria e
 * barre per sottocategoria: se "Forza" pesa il 30% del periodo, "Forza
 * Massima" deve leggersi come una frazione dello STESSO 100%, non di un 100%
 * ristretto al solo lavoro fisico (altrimenti si leggerebbe "Forza 30%" e
 * "Forza Massima 50%" sullo stesso dato, e chi guarda non saprebbe quale dei
 * due credere).
 *
 * Da quando un blocco può stare in più categorie, un blocco entra qui se
 * ALMENO UNA delle sue categorie è ammessa.
 */
function distribuzionePerSottocategoria(blocchi, categorieAmmesse) {
    const ammessi = categorieAmmesse
        ? blocchi.filter((b) => b.categoryIds.some((c) => categorieAmmesse.includes(c)))
        : blocchi;
    return distribuzionePer(ammessi, (b) => b.subcategoryIds, sommaMinuti(blocchi));
}
/** La voce con più minuti. `null` su distribuzione vuota. */
function dominante(voci) {
    if (voci.length === 0)
        return null;
    return voci.reduce((max, v) => (v.minuti > max.minuti ? v : max));
}
/** Elenco ordinato cronologicamente delle chiavi di bucket dentro [f.da, f.a]. */
function bucketDellaFinestra(f) {
    const out = [];
    if (f.granularita === 'day') {
        for (let cur = f.da; cur <= f.a; cur = (0, calendario_1.sommaGiorni)(cur, 1))
            out.push(cur);
        return out;
    }
    if (f.granularita === 'week') {
        const ultimo = (0, calendario_1.lunediDi)(f.a);
        for (let cur = (0, calendario_1.lunediDi)(f.da); cur <= ultimo; cur = (0, calendario_1.sommaGiorni)(cur, 7)) {
            out.push(cur);
        }
        return out;
    }
    // month: enumerazione per anno/mese, non per giorni (mesi di lunghezza diversa).
    let anno = Number(f.da.slice(0, 4));
    let mese = Number(f.da.slice(5, 7));
    const annoFine = Number(f.a.slice(0, 4));
    const meseFine = Number(f.a.slice(5, 7));
    while (anno < annoFine || (anno === annoFine && mese <= meseFine)) {
        out.push(`${anno}-${String(mese).padStart(2, '0')}`);
        mese += 1;
        if (mese > 12) {
            mese = 1;
            anno += 1;
        }
    }
    return out;
}
/**
 * Serie temporale dei minuti (totale + per categoria) sui bucket della
 * finestra, inclusi i bucket senza alcun blocco (minuti 0, non assenti): un
 * trend chart non deve "saltare" una settimana vuota.
 */
function serieTemporale(blocchi, f) {
    var _a;
    const bucketsOrdinati = bucketDellaFinestra(f);
    const punti = new Map(bucketsOrdinati.map((bucket) => [bucket, { bucket, minuti: 0, perCategoria: {} }]));
    for (const b of blocchi) {
        const bucket = bucketDi(b.localDate, f.granularita);
        const punto = punti.get(bucket);
        if (!punto)
            continue; // difensivo: blocco fuori dalla finestra richiesta
        /*
          `minuti` conta il blocco UNA volta: è il tempo davvero allenato nel
          bucket, e resta il totale onesto della settimana.
          `perCategoria` invece conta i minuti pieni su ciascuna categoria, quindi
          la somma delle sue voci può superare `minuti`. Sono due domande diverse:
          "quanto abbiamo allenato" e "quanto ha toccato ogni categoria".
        */
        punto.minuti += b.durationMinutes;
        for (const c of new Set(b.categoryIds)) {
            punto.perCategoria[c] = ((_a = punto.perCategoria[c]) !== null && _a !== void 0 ? _a : 0) + b.durationMinutes;
        }
    }
    return bucketsOrdinati.map((k) => punti.get(k));
}
/**
 * Δ% = (corrente − precedente) / precedente × 100, 1 decimale.
 * `null` se precedente ≤ 0 (spec: "nascosto se precedente = 0").
 *
 * Estratta da `apps/backend/src/effort/effort-calculations.ts` — spostata,
 * non copiata: quel file ora ri-esporta questa stessa funzione, così la tab
 * Sforzo e Allenamenti condividono un'unica implementazione.
 */
function deltaPct(corrente, precedente) {
    if (!(precedente > 0))
        return null;
    return (0, kernel_1.round)(((corrente - precedente) / precedente) * 100, 1);
}
